import React, { Suspense, lazy, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { AI_FUNNEL_ENABLED } from "../../constants/featureFlags";
import { getActiveSubscriptionData } from "../../redux/selectors/subscriptionSelectors";
import {
  UPGRADE_DRAFT_KEY,
  clearUpgradeDraft,
  isBuilderLive,
  readUpgradeDraft,
  shouldResumeUpgradeDraft,
} from "./upgradeDraft";

// Loaded only when a draft is restored: the app shell stays light.
const NewAIBuilder = lazy(() => import("./index"));
const AIFunnelBuilder = lazy(() => import("./AIFunnelBuilder"));

/**
 * T22.528 (Gordon): "after upgrading, return to the AI Generator with
 * everything restored exactly". Mounted once in the app shell. When a draft
 * saved by the builder's Upgrade Now exists and the plan tier is now higher
 * than when it was saved, re-open the AI Wheel Builder from it, on whatever
 * page the user is on (the Stripe checkout tab lands on /settings).
 *
 * Not in the tab whose builder saved the draft and is still open: that
 * builder already holds every field and just stays where it was.
 */
export default function AiBuilderUpgradeResume() {
  const tier = useSelector((state) => getActiveSubscriptionData(state)?.data?.sub_tier);
  const [draft, setDraft] = useState(null);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    const recheck = () => setTick((t) => t + 1);
    window.addEventListener("focus", recheck);
    window.addEventListener("storage", recheck);
    document.addEventListener("visibilitychange", recheck);
    return () => {
      window.removeEventListener("focus", recheck);
      window.removeEventListener("storage", recheck);
      document.removeEventListener("visibilitychange", recheck);
    };
  }, []);

  useEffect(() => {
    if (draft) return;
    const saved = readUpgradeDraft();
    if (!shouldResumeUpgradeDraft(saved, tier)) return;
    if (isBuilderLive(saved.ownerId)) return;
    // Taken by this tab: no other tab re-opens it too. The files stay in the
    // file store until the restored builder is closed or generates.
    try {
      localStorage.removeItem(UPGRADE_DRAFT_KEY);
    } catch (e) {
      // ignore
    }
    setDraft(saved);
  }, [tier, tick, draft]);

  if (!draft) return null;

  const close = () => {
    clearUpgradeDraft();
    setDraft(null);
  };
  const Builder =
    AI_FUNNEL_ENABLED && draft.fields?.funnel ? AIFunnelBuilder : NewAIBuilder;
  return (
    <Suspense fallback={null}>
      <Builder
        isOpen
        restoreDraft={draft}
        onClose={close}
        onSuccess={close}
        onCancelWizard={close}
      />
    </Suspense>
  );
}
